import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ViewType } from '../types/components';

interface HashRouteState {
  view: ViewType;
  params: Record<string, string>;
}

export interface SetHashViewOptions {
  params?: Record<string, string | number | boolean | null | undefined>;
  preserveParams?: boolean;
  replace?: boolean;
}

export interface SetHashParamsOptions {
  view?: ViewType;
  replaceAll?: boolean;
  replace?: boolean;
}

interface UseHashRouteOptions {
  preserveParamsFor?: ViewType[];
}

const KNOWN_VIEWS: ViewType[] = [
  'homepage',
  'search',
  'autocomplete',
  'bisac',
  'video',
  'reports',
  'calendar',
  'data-quality',
  'categories-quality',
  'bam-database',
  'search-discover',
  'quality-check',
  'quality-checkv2', // NEW: Comprehensive QC workflow with designer layout
  'manual-entry',
  'operations',
  'enrichment-board',
  'perfect-match',
  'match',
  'category-explorer',
  'programs-report',
  'program-card',
  'program-card-v2',
  'category-card',
  'print-results',
  'print-cat-results',
  'sell-books-start',
  'industry-leader-start',
  'build-business-start',
  'sell-books-questionnaire',
  'industry-leader-questionnaire',
  'build-business-questionnaire',
  'welcome-mail-test',
  'submit-success-email',
  'perfect-fit-report-email'
];

const KNOWN_VIEWS_SET = new Set<ViewType>(KNOWN_VIEWS);

const DEFAULT_PRESERVE: ViewType[] = ['program-card', 'program-card-v2', 'category-card', 'print-results', 'print-cat-results'];

type ParamValue = string | number | boolean | null | undefined;

type ParamRecord = Record<string, string>;

const sanitizeParamValue = (value: ParamValue): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const text = String(value);
  return text.trim().length > 0 ? text : undefined;
};

const buildHash = (view: ViewType, params: ParamRecord): string => {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => [key, value as string]);

  if (entries.length === 0) {
    return view;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of entries) {
    searchParams.set(key, value);
  }

  return `${view}?${searchParams.toString()}`;
};

const parseHash = (hash: string, fallbackView: ViewType): HashRouteState => {
  const normalized = hash.startsWith('#') ? hash.slice(1) : hash;
  const [rawView, queryString = ''] = normalized.split('?');

  const candidate = (rawView || fallbackView) as ViewType;
  const view = KNOWN_VIEWS_SET.has(candidate) ? candidate : fallbackView;

  const params = new URLSearchParams(queryString);
  const nextParams: ParamRecord = {};
  params.forEach((value, key) => {
    nextParams[key] = value;
  });

  return { view, params: nextParams };
};

export const useHashRoute = (
  fallbackView: ViewType,
  options: UseHashRouteOptions = {}
) => {
  const preserveSet = useMemo(() => {
    const presets = options.preserveParamsFor ?? DEFAULT_PRESERVE;
    return new Set<ViewType>(presets);
  }, [options.preserveParamsFor]);

  const [state, setState] = useState<HashRouteState>(() => parseHash(window.location.hash, fallbackView));
  const [isUpdatingHash, setIsUpdatingHash] = useState(false);

  const applyHash = useCallback((hash: string, replace: boolean) => {
    const normalized = hash.startsWith('#') ? hash.slice(1) : hash;
    const newState = parseHash(normalized, fallbackView);
    
    if (replace) {
      window.history.replaceState(null, '', `#${normalized}`);
      setState(newState);
      return;
    }
    
    // Set flag to prevent double update from hashchange event
    setIsUpdatingHash(true);
    
    // Always update state first for immediate UI feedback
    setState(newState);
    
    // Update URL hash
    const currentHash = window.location.hash.slice(1);
    if (currentHash !== normalized) {
      window.location.hash = normalized;
    }
    
    // Reset flag after a short delay
    setTimeout(() => setIsUpdatingHash(false), 0);
  }, [fallbackView]);

  useEffect(() => {
    const handleHashChange = () => {
      // Only update state if we're not already updating from applyHash
      if (!isUpdatingHash) {
        setState(parseHash(window.location.hash, fallbackView));
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [fallbackView, isUpdatingHash]);

  const setView = useCallback((view: ViewType, opts: SetHashViewOptions = {}) => {
    const preserve = opts.preserveParams ?? preserveSet.has(view);
    const baseParams = preserve ? { ...state.params } : {};
    const customParams = opts.params ?? {};
    const merged: ParamRecord = { ...baseParams };

    for (const [key, rawValue] of Object.entries(customParams)) {
      const sanitized = sanitizeParamValue(rawValue);
      if (sanitized === undefined) {
        delete merged[key];
      } else {
        merged[key] = sanitized;
      }
    }

    const nextHash = buildHash(view, merged);
    applyHash(nextHash, Boolean(opts.replace));
  }, [applyHash, preserveSet, state.params]);

  const setParams = useCallback((updates: Record<string, ParamValue>, opts: SetHashParamsOptions = {}) => {
    const targetView = opts.view ?? state.view;
    const base: ParamRecord = opts.replaceAll ? {} : targetView === state.view ? { ...state.params } : {};

    for (const [key, rawValue] of Object.entries(updates)) {
      const sanitized = sanitizeParamValue(rawValue);
      if (sanitized === undefined) {
        delete base[key];
      } else {
        base[key] = sanitized;
      }
    }

    const nextHash = buildHash(targetView, base);
    applyHash(nextHash, Boolean(opts.replace));
  }, [applyHash, state.params, state.view]);

  return {
    view: state.view,
    params: state.params,
    setView,
    setParams
  };
};

export interface UseHashRouteReturn {
  view: ViewType;
  params: Record<string, string>;
  setView: (view: ViewType, opts?: SetHashViewOptions) => void;
  setParams: (updates: Record<string, ParamValue>, opts?: SetHashParamsOptions) => void;
}
